import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useReachProSupportThreadPopupContext } from 'src/contexts/ReachProSupportThreadPopupContext';
import { Button } from 'src/core/ui';
import { useReachProSupportCaseLookup } from 'src/hooks/api/useReachProSupportCaseLookup';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CreateReachProSupportCaseModal } from 'src/modals/CreateReachProSupportCaseModal';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, Permission } from 'src/WebApiController';

export function ContactSupportButton({
  eventId,
  listingId,
  saleId,
  initialTopic,
  initialPlaceholder,
  disabled,
  marketplace,
}: {
  eventId?: number;
  listingId?: number;
  saleId?: number;
  initialTopic?: string;
  /** Contextual placeholder shown in the message textarea (not pre-filled text). */
  initialPlaceholder?: string;
  disabled?: boolean;
  /** When provided, the button is hidden for non-StubHub marketplaces. */
  marketplace?: string;
}) {
  const hasReachProSupportFeature = useUserHasFeature(Feature.ReachProSupport);
  const hasSupportViewPermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_View
  );
  const hasSupportCreatePermission = useUserHasAnyOfPermissions(
    Permission.SellerSupportCase_Create
  );
  const isMobile = useMatchMedia('mobile');
  const navigate = useNavigate();
  const { closeModal } = useContext(ModalContext);
  const { openSupportThreadPopup } = useReachProSupportThreadPopupContext();
  const lookup = useReachProSupportCaseLookup();
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  // The button both opens existing threads (View) and composes new cases (Create)
  if (
    !hasReachProSupportFeature ||
    !hasSupportViewPermission ||
    !hasSupportCreatePermission
  ) {
    return null;
  }

  // Hide for non-StubHub marketplaces — the ReachPro API only supports StubHub entities
  if (marketplace && marketplace !== 'StubHub') {
    return null;
  }

  const handleClick = async () => {
    const hasLookupKey = listingId != null || saleId != null;

    if (hasLookupKey) {
      try {
        const existing = await lookup.mutateAsync({ listingId, saleId });
        if (existing?.workItemId != null) {
          if (isMobile) {
            closeModal();
            navigate(`/reachpro-support/${existing.workItemId}`);
          } else {
            // Desktop: open the popup over the current view, keep the entity modal open.
            openSupportThreadPopup(existing.workItemId);
          }
          return;
        }
      } catch {
        // Fall through to compose so a lookup failure never blocks the seller.
      }
    }

    setIsComposeOpen(true);
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleClick}
        disabled={disabled || lookup.isPending}
      >
        <Content id={ContentId.ContactSupport} />
      </Button>
      <CreateReachProSupportCaseModal
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        initialTopic={initialTopic}
        initialPlaceholder={initialPlaceholder}
        initialEventId={eventId}
        initialListingId={listingId}
        initialSaleId={saleId}
        onCreated={(workItemId) => {
          if (isMobile) {
            closeModal();
            navigate(`/reachpro-support/${workItemId}`);
          } else {
            // Desktop: open the popup over the current view, keep the entity modal open.
            openSupportThreadPopup(workItemId);
          }
        }}
      />
    </>
  );
}
